import { encode } from '@googlemaps/polyline-codec'

import { TravelMode } from './enums'
import { deg2rad, distanceBetweenCoordinates, rad2deg } from './utility-manager'
import { routesModule } from './store-init'
import {
  GroupedBooking,
  Rental,
  RouteLocation,
  RouteSuggestion,
  TransportationType,
} from '@/types/transportation'
import { Route, Trip, Waypoint } from '@/types/trip'

/**
 * Groups bookings with same orgin & destination.
 *
 * @param bookings A list of bookings that should be grouped.
 * @param trip The trip the bookings belong to.
 */
export function groupBookings(bookings: any[], trip: Trip) {
  /* link bookings with same origin & destination */
  const groupedBookings: GroupedBooking[] = []

  for (const booking of bookings) {
    // search current booking in groupedBookings
    const index = groupedBookings.findIndex((el: any) => {
      return el.origin && el.destination
        ? booking.originWaypoint &&
            booking.destinationWaypoint &&
            el.origin._id === booking.originWaypoint._id &&
            el.destination._id === booking.destinationWaypoint._id
        : false
    })
    // if booking does not exist in groupedBookings
    if (index === -1) {
      // get origin of current booking
      const origin = trip.waypoints.find((element: Waypoint) => {
        return (
          booking.originWaypoint && element._id === booking.originWaypoint._id
        )
      })
      // get destination of current booking
      const destination = trip.waypoints.find(
        (element: Waypoint) =>
          booking.destinationWaypoint &&
          element._id === booking.destinationWaypoint._id,
      )

      // create a new object with origin, destination, booking
      if (origin && destination) {
        groupedBookings.push({
          _id: booking._id,
          origin,
          destination,
          data: [booking],
        })
      } else {
        groupedBookings.push({
          _id: booking._id,
          origin: null,
          destination: null,
          data: [booking],
        })
      }
    } else {
      // if booking found, push the booking to groupedBookings.data
      groupedBookings[index].data.push(booking)
    }
  }

  return groupedBookings
}

export function getDefaultEmojiForTransportationType(type: TransportationType) {
  switch (type) {
    case 'driving':
      return '🚘'
    case 'transit':
      return '🚆'
    case 'flight':
      return '✈️'
    case 'cycling':
      return '🚲'
    case 'walking':
      return '🚶‍♂️'
    case 'boat':
      return '⛴️'
  }
}

export function isPersistedRental(rental: Partial<Rental>): rental is Rental {
  return !!rental._id
}

export function getTravelModeForTransportationType(
  transportationType: TransportationType,
): TravelMode {
  switch (transportationType) {
    case 'transit':
      return 'transit'
    case 'driving':
      return 'driving'
    case 'cycling':
      return 'cycling'
    case 'walking':
      return 'walking'
    default:
      return 'direct'
  }
}

export function createDirectRoute(
  origin: RouteLocation,
  destination: RouteLocation,
) {
  const polyline = [
    [origin.latitude, origin.longitude],
    [destination.latitude, destination.longitude],
  ]
  const encodedPolyline = encode(polyline)
  const centerArray = centerForPolylineEdges(polyline)
  const distanceInMeters =
    distanceBetweenCoordinates(origin, destination) * 1000

  const newRoute: RouteSuggestion = {
    locations: [origin, destination],
    polyline: encodedPolyline,
    travelMode: 'direct',
    center: { latitude: centerArray[0], longitude: centerArray[1] },
    distance: distanceInMeters,
  }
  return newRoute
}

// CREDITS: https://stackoverflow.com/a/30033564/5537752
function centerForPolylineEdges(latLngInDegr: number[][]): number[] {
  let sumX = 0
  let sumY = 0
  let sumZ = 0

  for (let i = 0; i < latLngInDegr.length; i++) {
    const lat = deg2rad(latLngInDegr[i][0])
    const lng = deg2rad(latLngInDegr[i][1])
    // sum of cartesian coordinates
    sumX += Math.cos(lat) * Math.cos(lng)
    sumY += Math.cos(lat) * Math.sin(lng)
    sumZ += Math.sin(lat)
  }

  const avgX = sumX / latLngInDegr.length
  const avgY = sumY / latLngInDegr.length
  const avgZ = sumZ / latLngInDegr.length

  // convert average x, y, z coordinate to latitude and longtitude
  const lng = Math.atan2(avgY, avgX)
  const hyp = Math.sqrt(avgX * avgX + avgY * avgY)
  const lat = Math.atan2(avgZ, hyp)

  return [rad2deg(lat), rad2deg(lng)]
}

/**
 * Calculates the total distance of a trip based on its waypoints and their
 * transportations.
 *
 * If a waypoint has no transportation with a defined route, a direct route to
 * the next waypoint is created and its distance is added.
 * It supports the legacy and new transportation system.
 *
 * @param trip The trip for which the total distance should be calculated.
 * @returns The total distance of the trip in kilometers.
 */
export function calculateTotalDistance(trip: Trip) {
  if (trip.userInfo?.useNewTransportationSystem) {
    return calculateTotalDistanceForNewTransportation(trip)
  } else {
    return calculateTotalDistanceForLegacyTransportation(trip)
  }
}

function calculateTotalDistanceForNewTransportation(trip: Trip) {
  let totalDistance = 0

  if (trip.waypoints.length < 2) {
    return totalDistance
  }

  for (let index = 0; index < trip.waypoints.length; index++) {
    const currentWaypoint = trip.waypoints[index]

    let hasWaypointAtleastOneRoute = false
    for (const transportation of currentWaypoint.transportations) {
      if (transportation.route) {
        hasWaypointAtleastOneRoute = true
        totalDistance += transportation.route.distance / 1000
      }
    }

    if (!hasWaypointAtleastOneRoute && index < trip.waypoints.length - 1) {
      totalDistance += distanceBetweenCoordinates(
        currentWaypoint.location,
        trip.waypoints[index + 1].location,
      )
    }
  }

  return totalDistance
}

function calculateTotalDistanceForLegacyTransportation(trip: Trip) {
  let totalDistance = 0
  const routes: Route[] = routesModule.routes

  if (trip.waypoints.length < 2) {
    return totalDistance
  }

  trip.waypoints.forEach((waypoint, index) => {
    const nextWaypoint = trip.waypoints[index + 1]
    if (!nextWaypoint) {
      return
    }

    const route = routes.find(
      (route) =>
        route.origin === waypoint._id && route.destination === nextWaypoint._id,
    )

    if (route && route.properties && route.properties.distance) {
      totalDistance += route.properties.distance / 1000
    } else {
      totalDistance += distanceBetweenCoordinates(
        waypoint.location,
        nextWaypoint.location,
      )
    }
  })

  return totalDistance
}
